// Client branding (set on the dashboard's Clients page): logo, colours,
// favicon, title, default theme. Comes from the API as `branding` on the
// client (login / /me) or, before login, from /branding/:slug (?c=<slug>).
//
// Colours are applied by overriding the theme variables in globals.css
// (--primary*, --secondary*) as inline styles on <html>, so they win over
// both the dark and light theme blocks. Clearing the branding restores ours.
import { useEffect } from 'react'

const BRAND_VARS = ['--primary', '--primary-dark', '--primary-light', '--primary-glow', '--primary-rgb',
  '--gradient-bg-1', '--shadow-glow', '--secondary', '--secondary-dark', '--secondary-light', '--accent']
const DEFAULT_TITLE = 'Redstone AI — Client Portal'
const DEFAULT_FAVICON = '/favicon.png'

export const DEFAULT_LOGOS = { light: '/logo-light.png', dark: '/logo-dark.png' }

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
// Mix toward black (amount < 0) or white (amount > 0).
const shade = ([r, g, b], amount) => {
  const t = amount < 0 ? 0 : 255, p = Math.abs(amount)
  const c = (v) => Math.round(v + (t - v) * p).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}

export function applyBranding(branding) {
  const root = document.documentElement.style
  for (const v of BRAND_VARS) root.removeProperty(v)

  const primary = hexToRgb(branding?.primary_color)
  if (primary) {
    const rgb = primary.join(', ')
    root.setProperty('--primary', branding.primary_color)
    root.setProperty('--primary-dark', shade(primary, -0.18))
    root.setProperty('--primary-light', shade(primary, 0.35))
    root.setProperty('--primary-rgb', rgb)
    root.setProperty('--primary-glow', `rgba(${rgb}, 0.35)`)
    root.setProperty('--gradient-bg-1', `rgba(${rgb}, 0.12)`)
    root.setProperty('--shadow-glow', `0 0 40px rgba(${rgb}, 0.2)`)
  }
  const accent = hexToRgb(branding?.accent_color)
  if (accent) {
    root.setProperty('--accent', branding.accent_color)
    root.setProperty('--secondary', branding.accent_color)
    root.setProperty('--secondary-dark', shade(accent, -0.18))
    root.setProperty('--secondary-light', shade(accent, 0.35))
  }

  document.title = branding?.title ? `${branding.title} — Client Portal` : DEFAULT_TITLE
  let icon = document.querySelector('link[rel="icon"]')
  if (!icon) {
    icon = document.createElement('link')
    icon.rel = 'icon'
    document.head.appendChild(icon)
  }
  icon.href = branding?.favicon_url || DEFAULT_FAVICON
}

// Apply `branding` while the calling component is mounted.
export function useBranding(branding) {
  const key = JSON.stringify(branding || null)
  useEffect(() => {
    applyBranding(branding)
    return () => applyBranding(null)   // e.g. logout → back to ours
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

// Logo for the current theme: the client's (dark-mode variant in dark theme)
// or ours when there is none or it fails to load.
export function logoFor(branding, theme) {
  const own = theme === 'light' ? branding?.logo_url : (branding?.logo_dark_url || branding?.logo_url)
  return own || DEFAULT_LOGOS[theme === 'light' ? 'light' : 'dark']
}
export function onLogoError(e, theme) {
  const fallback = DEFAULT_LOGOS[theme === 'light' ? 'light' : 'dark']
  if (!e.currentTarget.src.endsWith(fallback)) e.currentTarget.src = fallback
}
